import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  UpdateSupplierMappingMutationRequest,
  UpdateSupplierMappingMutationResponse,
} from "../types/UpdateSupplierMapping"

type UpdateSupplierMappingClient = typeof client<
  UpdateSupplierMappingMutationResponse,
  Error,
  UpdateSupplierMappingMutationRequest
>
type UpdateSupplierMapping = {
  data: UpdateSupplierMappingMutationResponse
  error: Error
  request: UpdateSupplierMappingMutationRequest
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<UpdateSupplierMappingClient>>
  client: {
    parameters: Partial<Parameters<UpdateSupplierMappingClient>[0]>
    return: Awaited<ReturnType<UpdateSupplierMappingClient>>
  }
}
/**
 * @summary Update supplier mapping
 * @link /v1/client-settings/update-supplier-mapping
 */
export function useUpdateSupplierMapping(
  options: {
    mutation?: UseMutationOptions<
      UpdateSupplierMapping["response"],
      UpdateSupplierMapping["error"],
      UpdateSupplierMapping["request"]
    >
    client?: UpdateSupplierMapping["client"]["parameters"]
  } = {}
): UseMutationResult<
  UpdateSupplierMapping["response"],
  UpdateSupplierMapping["error"],
  UpdateSupplierMapping["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    UpdateSupplierMapping["response"],
    UpdateSupplierMapping["error"],
    UpdateSupplierMapping["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        UpdateSupplierMapping["data"],
        UpdateSupplierMapping["error"],
        UpdateSupplierMapping["request"]
      >({
        method: "post",
        url: `/v1/client-settings/update-supplier-mapping`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
